"use client";

import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  type Locale,
  locale as localeDefinition,
  type MessageKey,
  messagePack,
} from "@tovi/shared";

/**
 * The translator, injected the same way routing is.
 *
 * It lives here rather than in `@tovi/app-core` for the reason `LinkProvider`
 * does: three surfaces need it and only two of them may import Next. The till
 * consumes `pos-core` + `shared` + `ui` and explicitly not `app-core`
 * (`02-architecture.md`), and the diner app is a public, no-login surface built
 * for a mid-range Android over 3G — pulling `app-core` in for a `t()` would
 * bring Better Auth's client with it. `@tovi/ui` is what all four apps already
 * share, so this is the one place the implementation can exist once.
 *
 * Translations are data, not build artefacts (`02-architecture.md` § i18n): the
 * base pack ships in the repo as the fallback and a `translations` table
 * overlays per-key overrides, so support can fix a Khmer string without a
 * deploy. The overlay is fetched into `overrides` by the app shell.
 *
 * `t()` falls back to English before falling back to the key itself, so an
 * untranslated string reads as English rather than as `shell.emptyTitle`.
 */
type I18nValue = {
  locale: Locale;
  t: (key: MessageKey, values?: Record<string, string | number>) => string;
  /** Layout facts, not preferences — see `LOCALES` in `@tovi/shared`. */
  isLongForm: boolean;
  isComplexScript: boolean;
  direction: "ltr" | "rtl";
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  locale,
  overrides,
  children,
}: {
  locale: Locale;
  overrides?: Record<string, string>;
  children: ReactNode;
}) {
  const pack = useMemo(() => messagePack(locale), [locale]);
  const fallback = useMemo(() => messagePack(DEFAULT_LOCALE), []);

  const t = useCallback(
    (key: MessageKey, values?: Record<string, string | number>) => {
      const template = overrides?.[key] ?? pack[key] ?? fallback[key] ?? key;
      if (!values) return template;
      return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        name in values ? String(values[name]) : match,
      );
    },
    [pack, fallback, overrides],
  );

  const value = useMemo<I18nValue>(() => {
    const definition = localeDefinition(locale);
    return {
      locale,
      t,
      isLongForm: definition.isLongForm,
      isComplexScript: definition.isComplexScript,
      direction: definition.direction,
    };
  }, [locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside an I18nProvider");
  return value;
}

export function useTranslate() {
  return useI18n().t;
}
